/* The right column: the state and the history of the selected level.
 *
 * Nothing is edited here — the middle owns that. What this answers is "what
 * is this, who touched it last, and how do I take an edit back": the status,
 * the counts, and the history scoped to the selection, which the edit table
 * carries at exactly this grain. Putting a change back is one button, and the
 * revert records itself, so undoing an undo is the same button again.
 */

import { useMemo } from "react";
import type { ContentEdit, ContentVersion } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { asPath, DRAFT, type Ed } from "./Content";

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** How far the shown history reaches: everything under the selected part. */
function scopeOf(ed: Ed): string {
  const sub = ed.sel.sub;
  if (!sub) return "";
  if (sub.kind === "kit") return `kit[${sub.i}]`;
  if (sub.kind === "goal") return `goal.levels[${sub.i}]`;
  if (sub.kind === "part") return `parts[${sub.i}]`;
  return asPath(sub.path);
}

export function EdSide({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const row = ed.rows.find((r) => r.path === ed.sel.doc);
  const scope = scopeOf(ed);

  const merged = useMemo(() => {
    const edits = ed.edits
      .filter((e) => scope === "" || e.at === "" || e.at.startsWith(scope))
      .map((e) => ({ edit: true as const, ...e }));
    const versions = scope === ""
      ? ed.versions.map((v) => ({ edit: false as const, ...v }))
      : [];
    return [...edits, ...versions]
      .sort((a, b) => b.saved_at.localeCompare(a.saved_at));
  }, [ed.edits, ed.versions, scope]);

  if (!ed.sel.doc) {
    /* No document selected: the catalogue in numbers, so the column is a
       summary rather than an empty strip. */
    const lessons = ed.rows.filter((r) => r.meta?.type === "lesson");
    const drafts = ed.rows.filter((r) => r.kind === "doc" && r.status === DRAFT);
    return (
      <aside className="edx-side">
        <div className="edx-what">{ui("content.tree")}</div>
        <dl className="edx-stats">
          <div><dt>{ui("content.schools")}</dt><dd>{ed.tax?.strandIds.length ?? 0}</dd></div>
          <div><dt>{ui("content.lessons")}</dt><dd>{lessons.length}</dd></div>
          <div><dt>{ui("content.draft")}</dt><dd>{drafts.length}</dd></div>
        </dl>
      </aside>
    );
  }

  const filled = (() => {
    if (!ed.parsed || ed.parsed["type"] !== "lesson") return null;
    const kit = Array.isArray(ed.parsed["kit"]) ? ed.parsed["kit"] : [];
    const done = kit.filter((it) => isObject(it) && isObject(it["ready"])).length;
    return `${done} / 10`;
  })();

  return (
    <aside className="edx-side">
      <div>
        <div className="edx-what">{scope ? ui("content.historyPart") : ui("content.historyDoc")}</div>
        <div className="edx-path"><code>{ed.sel.doc}{scope ? ` · ${scope}` : ""}</code></div>
      </div>

      {row ? (
        <dl className="edx-stats">
          <div><dt>{ui("content.state")}</dt>
            <dd>{ui(row.status === DRAFT ? "content.draft" : "content.published")}</dd></div>
          <div><dt>{ui("content.updated")}</dt>
            <dd>{new Date(row.updated_at).toLocaleString()} · {row.updated_by ?? ui("content.never")}</dd></div>
          {filled ? <div><dt>{ui("content.filledCol")}</dt><dd>{filled}</dd></div> : null}
        </dl>
      ) : null}

      <div className="edx-hist">
        {merged.length ? merged.map((h) => (
          <div key={h.id} className="edx-hrow">
            {h.edit
              ? ((h as ContentEdit).at
                ? <code>{(h as ContentEdit).at}</code>
                : <em className="edx-mut">{ui("content.whole")}</em>)
              : <em className="edx-mut">{ui("content.whole")} · {(h as ContentVersion).sha256.slice(0, 8)}</em>}
            <span className="edx-hwho">
              {h.saved_by ?? ui("content.never")} · {new Date(h.saved_at).toLocaleString()}
            </span>
            <button type="button" className="plain" disabled={ed.busy}
                    onClick={() => void (h.edit ? ed.revertEdit(h.id) : ed.revertVersion(h.id))}>
              {ui("content.revert")}
            </button>
          </div>
        )) : <p className="edx-empty">{ui("content.noHistory")}</p>}
      </div>
    </aside>
  );
}
